import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, MessageSquare, Users, CreditCard, ExternalLink, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';

const ContactPage = () => {
  const { t } = useLanguage();

  const openingHoursData = [
    { day: t("contactPage.days.monday"), hours: t("contactPage.hours.monTue") },
    { day: t("contactPage.days.tuesday"), hours: t("contactPage.hours.monTue") },
    { day: t("contactPage.days.wednesday"), hours: t("contactPage.hours.wedThuFri") },
    { day: t("contactPage.days.thursday"), hours: t("contactPage.hours.wedThuFri") },
    { day: t("contactPage.days.friday"), hours: t("contactPage.hours.wedThuFri") },
    { day: t("contactPage.days.saturday"), hours: t("contactPage.hours.saturday"), note: t("contactPage.hours.saturdayNote") },
    { day: t("contactPage.days.sunday"), hours: t("contactPage.hours.sunday") },
  ];

  const whatsappLink = "https://chat.whatsapp.com/DkvPVnLDNfXJC8w1uBZAGO";
  const reviewLink = "https://g.page/r/CSppBr1ncMe8EBM/review";
  const openStreetMapEmbedUrl = "https://www.openstreetmap.org/export/embed.html?bbox=5.395317077636719%2C52.47317901190118%2C5.539131164550781%2C52.54940018316815&layer=mapnik";

  return (
    <div className="py-12 md:py-20 bg-white pattern-bg">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Mail className="h-16 w-16 brand-text mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-extrabold brand-text mb-4">{t('contactPage.title')}</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('contactPage.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 items-start">
          <motion.div
            className="md:col-span-2 space-y-8"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div id="contact-info" className="p-6 bg-card rounded-lg shadow-md border border-gray-100">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                <Users className="h-7 w-7 brand-text mr-3" /> {t('contactPage.contactInfoTitle')}
              </h2>
              <div className="space-y-3">
                <p className="flex items-start">
                  <MapPin className="h-5 w-5 brand-text mr-3 mt-1 shrink-0" />
                  <span dangerouslySetInnerHTML={{ __html: t('contactPage.address').replace(/\n/g, '<br />') }} />
                </p>
                <p className="flex items-center">
                  <Phone className="h-5 w-5 brand-text mr-3 shrink-0" />
                  <a href={`tel:${t('contactPage.phone')}`} className="text-gray-700 hover:brand-text transition-colors">{t('contactPage.phone')}</a>
                </p>
                <p className="flex items-center">
                  <Mail className="h-5 w-5 brand-text mr-3 shrink-0" />
                  <a href={`mailto:${t('contactPage.email')}`} className="text-gray-700 hover:brand-text transition-colors">{t('contactPage.email')}</a>
                </p>
                <p className="flex items-center">
                  <MessageSquare className="h-5 w-5 brand-text mr-3 shrink-0" />
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:brand-text transition-colors">
                    {t('contactPage.whatsapp')} <ExternalLink className="h-4 w-4 inline-block ml-1" />
                  </a>
                </p>
                 <p className="flex items-center">
                  <Star className="h-5 w-5 brand-text mr-3 shrink-0" />
                  <a href={reviewLink} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:brand-text transition-colors">
                    {t('contactPage.googleReview')} <ExternalLink className="h-4 w-4 inline-block ml-1" />
                  </a>
                </p>
                <p className="flex items-center">
                  <CreditCard className="h-5 w-5 brand-text mr-3 shrink-0" />
                  <span className="text-gray-700">{t('contactPage.kvk')}</span>
                </p>
              </div>
            </div>

            <div className="bg-card p-6 sm:p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <Clock className="h-6 w-6 brand-text mr-3" />
                <h2 className="text-2xl font-semibold text-gray-800">{t('contactPage.openingHoursTitle')}</h2>
              </div>
              <ul className="space-y-2">
                {openingHoursData.map((item, index) => (
                  <li key={index} className="flex justify-between text-muted-foreground text-base">
                    <span>{item.day}</span>
                    <div className="text-right">
                      <span>{item.hours}</span>
                      {item.note && <span className="block text-xs brand-text">{item.note}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          <motion.div
            className="md:col-span-1 h-full"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div id="location" className="p-6 bg-card rounded-lg shadow-md border border-gray-100 h-full flex flex-col">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                <MapPin className="h-7 w-7 brand-text mr-3"/> {t('contactPage.locationTitle')}
              </h2>
              <div className="aspect-video rounded-md overflow-hidden mb-4 flex-grow">
                <iframe 
                  src={openStreetMapEmbedUrl}
                  className="w-full h-full border-0" 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade" 
                  title={t('contactPage.mapTitle')}>
                </iframe>
              </div>
              <a href="https://www.google.com/maps/search/?api=1&amp;query=Griend+36-15,+8225+TE+Lelystad" target="_blank" rel="noopener noreferrer">
                <Button className="w-full primary-button">{t('contactPage.openInGoogleMaps')}</Button>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;